import { useEffect, useState } from 'react';
import { api, ApiError, type Menu, type Quote, type TableInfo } from '../api';
import { cartEstimate, formatMoney, type CartState } from '../cart';
import type { T } from '../i18n';

type Props = {
  token: string;
  info: TableInfo;
  menu: Menu;
  cart: CartState;
  t: T;
  onChange: (cart: CartState) => void;
  onQuantity: (key: string, qty: number) => void;
  onSubmit: () => Promise<void>;
  onClose: () => void;
};

export function CartSheet({ token, info, menu, cart, t, onChange, onQuantity, onSubmit, onClose }: Props) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currency = info.restaurant.currency ?? menu.currency;
  const products = new Map(menu.products.map((p) => [p.id, p]));
  const estimate = cartEstimate(menu, cart.lines);

  useEffect(() => {
    if (!cart.lines.length) return setQuote(null);
    let cancelled = false;
    const timer = setTimeout(() => {
      api.quote(token, cart.lines).then((q) => !cancelled && setQuote(q)).catch(() => !cancelled && setQuote(null));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, cart.lines]);

  const priced = quote?.source === 'restaurant' && typeof quote.total === 'number';

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await onSubmit();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="cart-title" onClick={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose} aria-label={t('close')}>×</button>
        <h2 id="cart-title">{t('cart')}</h2>
        {!cart.lines.length && <p className="note">{t('empty')}</p>}
        {cart.lines.map((line) => {
          const p = products.get(line.product_id);
          const variant = p?.variants.find((v) => v.id === line.variant_id);
          const addonNames = line.addons
            .map((a) => p?.addon_groups.flatMap((g) => g.addons).find((x) => x.id === a.id)?.name)
            .filter(Boolean)
            .join(', ');
          return (
            <div className="line" key={line.key}>
              <div className="meta">
                <div>{p?.name ?? line.product_id}</div>
                <div className="opts">{[variant?.name, addonNames, line.special_instructions].filter(Boolean).join(' · ')}</div>
              </div>
              <span className="qty">
                <button onClick={() => onQuantity(line.key, line.quantity - 1)} aria-label={line.quantity === 1 ? t('remove') : t('decrease')}>−</button>
                <span>{line.quantity}</span>
                <button onClick={() => onQuantity(line.key, line.quantity + 1)} aria-label={t('increase')}>+</button>
              </span>
            </div>
          );
        })}

        {cart.lines.length > 0 && (
          <>
            <div className="totals">
              <div><span>{t('subtotal')}</span><span>{formatMoney(priced ? quote!.subtotal : estimate, currency)}</span></div>
              {priced && quote!.tax !== null && quote!.tax !== undefined && <div><span>{t('tax')}</span><span>{formatMoney(Number(quote!.total) - Number(quote!.subtotal), currency)}</span></div>}
              <div className="grand"><span>{t('total')}</span><span>{formatMoney(priced ? Number(quote!.total) : estimate, currency)}</span></div>
              <p className="note">{priced ? t('confirmedByRestaurant') : t('estimated')}</p>
            </div>

            {cart.detailsSkipped ? (
              <button type="button" className="link" data-testid="qr-details-add" onClick={() => onChange({ ...cart, detailsSkipped: false })}>
                {t('addDetails')}
              </button>
            ) : (
              <fieldset className="details" data-testid="qr-details">
                <div className="details-head">
                  <legend>{t('yourDetails')}</legend>
                  <button
                    type="button"
                    className="link"
                    data-testid="qr-details-skip"
                    onClick={() => onChange({ ...cart, name: '', phone: '', waUpdates: false, detailsSkipped: true })}
                  >
                    {t('skipDetails')}
                  </button>
                </div>
                <label className="lbl" htmlFor="cust-name">{t('yourNameOptional')}</label>
                <input id="cust-name" className="field" autoComplete="given-name" maxLength={80} value={cart.name} onChange={(e) => onChange({ ...cart, name: e.target.value })} />
                <label className="lbl" htmlFor="cust-phone">{t('phoneOptional')}</label>
                <input id="cust-phone" className="field" type="tel" inputMode="tel" autoComplete="tel" maxLength={20} value={cart.phone} onChange={(e) => onChange({ ...cart, phone: e.target.value, waUpdates: e.target.value.trim() ? cart.waUpdates : false })} />
                <p className="note">{t('detailsWhy')}</p>
                {cart.phone.trim() && (
                  <label className="check">
                    <input type="checkbox" data-testid="qr-wa-consent" checked={cart.waUpdates} onChange={(e) => onChange({ ...cart, waUpdates: e.target.checked })} />
                    <span>{t('waUpdatesConsent')}</span>
                  </label>
                )}
              </fieldset>
            )}
            <label className="lbl" htmlFor="order-note">{t('orderNote')}</label>
            <textarea id="order-note" rows={2} maxLength={500} value={cart.note} onChange={(e) => onChange({ ...cart, note: e.target.value })} />

            {!info.online && <p className="banner" role="status">{t('restaurantOffline')}</p>}
            <p className="note">{t('payAtCounter')}</p>
            {error && <p className="error" role="alert">{error}</p>}
            <button className="btn" onClick={submit} disabled={sending}>
              {sending ? t('sending') : t('placeOrder')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
